/**
 * TOSS Web Application - Radio Group Component
 *
 * Radio button group component for single selection from options.
 * Supports horizontal and vertical layouts.
 *
 * Features:
 * - Horizontal or vertical layout
 * - Full keyboard accessibility
 * - Disabled state support
 * - Error state styling
 * - React Hook Form compatible
 */

import { forwardRef, useId, type InputHTMLAttributes } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface RadioOption {
  /** Option value */
  value: string | number;
  /** Display label */
  label: string;
  /** Whether this option is disabled */
  disabled?: boolean;
}

export interface RadioGroupProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  /** Available options */
  options: RadioOption[];
  /** Currently selected value */
  value?: string | number;
  /** Change handler */
  onChange?: (value: string) => void;
  /** Layout direction */
  orientation?: 'horizontal' | 'vertical';
  /** Group label */
  label?: string;
  /**
   * How the group label is set. `section` (default) — a small heading above a block of choices;
   * `field` — the same recipe as `FieldLabel`, for a group standing in a form next to text fields
   * (a window of four fields where one of them is a choice).
   */
  labelVariant?: 'section' | 'field';
  /** Error message */
  error?: string;
  /** Whether the group is required */
  required?: boolean;
  /** Disable all options */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Radio group component for single selection
 *
 * **Кружок нарисован, а не отдан браузеру (03.09.2026).** До этого дня здесь стоял голый
 * `<input type="radio">` с классами `text-primary-600 focus:ring-*`, которые без плагина
 * `@tailwindcss/forms` не делают ничего, — и выбранный пункт красила операционная система своим
 * синим. Владелец увидел это в окне энергоустановки («визуально неправильно, выделить цветом»).
 * Теперь тот же рецепт, что у `Checkbox`: `appearance-none`, рамка `--color-border`, в отмеченном
 * состоянии заливка `--color-primary-500` и точка `--color-text-on-primary`. Элемент остался
 * настоящим `<input>`: `ref`, `name`, `register()` и скринридер работают как прежде.
 *
 * @example
 * ```tsx
 * // Horizontal layout (default)
 * <RadioGroup
 *   label="Приоритет"
 *   options={[
 *     { value: 'urgent', label: 'Срочный' },
 *     { value: 'high', label: 'Высокий' },
 *     { value: 'normal', label: 'Обычный' },
 *   ]}
 *   value={priority}
 *   onChange={setPriority}
 * />
 *
 * // Vertical layout
 * <RadioGroup
 *   orientation="vertical"
 *   options={options}
 *   value={selected}
 *   onChange={setSelected}
 * />
 *
 * // With React Hook Form
 * <Controller
 *   name="priority"
 *   control={control}
 *   render={({ field }) => (
 *     <RadioGroup
 *       options={priorityOptions}
 *       value={field.value}
 *       onChange={field.onChange}
 *     />
 *   )}
 * />
 * ```
 */
export const RadioGroup = forwardRef<HTMLInputElement, RadioGroupProps>(
  (
    {
      options,
      value,
      onChange,
      orientation = 'horizontal',
      label,
      labelVariant = 'section',
      error,
      required,
      disabled = false,
      className = '',
      name: providedName,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const groupName = providedName ?? `radio-group-${generatedId}`;
    const errorId = `${groupName}-error`;

    const hasError = !!error;

    const handleChange = (optionValue: string | number): void => {
      if (onChange) {
        onChange(String(optionValue));
      }
    };

    const containerClasses = [
      orientation === 'horizontal'
        ? 'flex flex-wrap gap-6'
        : 'flex flex-col gap-3',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className="w-full">
        {label && (
          <div
            className={
              labelVariant === 'field'
                ? 'block text-sm font-medium text-[var(--color-text-primary)] mb-2'
                : 'block text-base font-semibold text-[var(--color-text-primary)] mb-3'
            }
          >
            {label}
            {required && <span className="text-[var(--color-error)] ml-1">*</span>}
          </div>
        )}

        <div
          role="radiogroup"
          aria-label={label}
          aria-invalid={hasError}
          aria-describedby={hasError ? errorId : undefined}
          className={containerClasses}
        >
          {options.map((option, index) => {
            const optionId = `${groupName}-${option.value}`;
            const isChecked = value === option.value || String(value) === String(option.value);
            const isDisabled = disabled || option.disabled;

            return (
              <label
                key={option.value}
                htmlFor={optionId}
                className={`
                  inline-flex items-center gap-2 cursor-pointer select-none
                  ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                {/* Кружок нарисован, а не отдан браузеру — тот же рецепт, что у `Checkbox`
                    (03.09.2026): без `@tailwindcss/forms` классы на нативном radio инертны, и
                    операционная система красила выбранный пункт своим синим. 18 px явными
                    пикселями, как у коробочки: числовые классы считаются от плавающего корневого
                    кегля. Точка лежит поверх (`peer-checked`) и не ловит события. */}
                <span className="relative inline-flex h-[18px] w-[18px] flex-shrink-0">
                  <input
                    ref={index === 0 ? ref : undefined}
                    type="radio"
                    id={optionId}
                    name={groupName}
                    value={option.value}
                    checked={isChecked}
                    onChange={() => handleChange(option.value)}
                    disabled={isDisabled}
                    className={`
                      peer h-[18px] w-[18px] appearance-none rounded-full border
                      border-[var(--color-border)] bg-[var(--color-bg-card)] cursor-pointer
                      transition-colors
                      checked:border-[var(--color-primary-500)] checked:bg-[var(--color-primary-500)]
                      focus:outline-none focus-visible:ring-2
                      focus-visible:ring-[color-mix(in_srgb,var(--color-accent-500)_35%,transparent)]
                      disabled:cursor-not-allowed disabled:bg-[var(--color-bg-secondary)]
                      disabled:checked:bg-[var(--color-text-disabled)]
                      disabled:checked:border-[var(--color-text-disabled)]
                      ${hasError ? 'border-[var(--color-error)]' : ''}
                    `}
                    {...props}
                  />
                  <span
                    className="pointer-events-none absolute inset-0 m-auto h-[7px] w-[7px] rounded-full bg-[var(--color-text-on-primary)] opacity-0 peer-checked:opacity-100"
                    aria-hidden="true"
                  />
                </span>
                <span className={`text-sm ${isDisabled ? 'text-[var(--color-text-disabled)]' : 'text-[var(--color-text-primary)]'}`}>
                  {option.label}
                </span>
              </label>
            );
          })}
        </div>

        {hasError && (
          <p id={errorId} className="mt-1.5 text-sm text-[var(--color-error)]">
            {error}
          </p>
        )}
      </div>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';

export default RadioGroup;
